import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, TrendingUp } from "lucide-react";

const color = (p) => (p >= 90 ? "#81B29A" : p >= 75 ? "#F4A261" : "#E07A5F");

export default function Mastery() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [sid, setSid] = useState("");
  const [data, setData] = useState(null);
  const [insight, setInsight] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/students").then((r) => { setStudents(r.data); if (r.data[0]) setSid(r.data[0].id); }); }, []);
  useEffect(() => { if (sid) { setInsight(""); api.get(`/mastery/${sid}`).then((r) => setData(r.data)); } }, [sid]);

  const getInsight = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/mastery/${sid}/insight`); setInsight(data.insight); }
    catch (e) { toast.error("Could not generate insight."); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <TopBar subtitle="Skill Mastery" />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <button onClick={() => navigate("/parent")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B]"><ArrowLeft size={16} /> Back</button>
          <select data-testid="mastery-student" value={sid} onChange={(e) => setSid(e.target.value)}
            className="rounded-full border border-[#EAE6DF] bg-white px-5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E07A5F]">
            {students.map((s) => <option key={s.id} value={s.id}>{s.first_name}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <span className="grid place-items-center w-11 h-11 rounded-2xl bg-[#81B29A] text-white"><TrendingUp size={20} /></span>
          <h1 className="font-head text-4xl font-bold text-[#3D405B]">Skill Mastery</h1>
        </div>

        {data && (
          <>
            <button data-testid="insight-btn" onClick={getInsight} disabled={busy || !data.topics.length}
              className="flex items-center gap-2 rounded-full bg-[#3D405B] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#2f3248] transition-colors disabled:opacity-50 mb-6">
              <Sparkles size={16} /> {busy ? "Thinking…" : "Get AI insight"}
            </button>
            {insight && <div data-testid="insight-text" className="rounded-2xl bg-[#F2EFE9] border border-[#EAE6DF] p-6 mb-8 leading-relaxed text-[#3D405B]">{insight}</div>}

            {data.topics.length === 0 ? (
              <p className="text-[#6B7280]">No graded work yet. Assign a lesson to start tracking mastery.</p>
            ) : (
              <div className="space-y-4">
                {data.topics.map((t, i) => (
                  <div key={i} data-testid={`mastery-topic-${i}`} className="rounded-xl bg-white border border-[#EAE6DF] p-5">
                    <div className="flex justify-between items-baseline mb-2">
                      <div><span className="text-xs font-semibold uppercase tracking-wide text-[#6B7280]">{t.subject}</span>
                        <p className="font-medium text-[#3D405B]">{t.topic}</p></div>
                      <span className="stat text-xl font-semibold" style={{ color: color(t.percentage) }}>{t.percentage}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-[#F2EFE9] overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${t.percentage}%`, background: color(t.percentage) }} />
                    </div>
                    <p className="text-xs text-[#6B7280] mt-1">{t.attempts} attempt{t.attempts > 1 ? "s" : ""}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
